<template>
    <div class="ngdialog-content">
        <dialog-header>
            <h4 v-if="this.organization">{{ $t('organizations.edit_organization') }}</h4>
            <h4 v-else>{{ $t('organizations.create_organization') }}</h4>
        </dialog-header>

        <form name="partnerForm" novalidate @submit.prevent="submitHandler">
            <div class="ngdialog__body">
                <fieldset class="form__group">
                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="name"
                            name="name"
                            :placeholder="$t('fields.organization_name')"
                            v-model.trim="$v.formData.name.$model"
                        />

                        <div v-if="$v.formData.name.$error">
                            <p class="form__error">{{ $t('fields.name_e_required') }}</p>
                        </div>
                    </div>

                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="email"
                            name="email"
                            :placeholder="$t('fields.email')"
                            v-model.trim="$v.formData.email.$model"
                        />

                        <div v-if="$v.formData.email.$error">
                            <p class="form__error" v-if="!$v.formData.email.required">
                                {{ $t('fields.email_e_required') }}
                            </p>

                            <p class="form__error" v-if="!$v.formData.email.email">
                                {{ $t('fields.email_e_email') }}
                            </p>
                        </div>
                    </div>

                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="address1"
                            name="address1"
                            :placeholder="$t('fields.address1')"
                            v-model="formData.address1"
                        />

                        <div class="form__help">{{ $t('fields.address1_help') }}</div>

                        <div v-if="$v.formData.address1.$dirty">
                            <p class="form__error" v-if="!$v.formData.address1.required">
                                {{ $t('fields.street_e_required') }}
                            </p>
                        </div>
                    </div>

                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="address2"
                            name="address2"
                            :placeholder="$t('fields.address2')"
                            v-model="formData.address2"
                        />

                        <div class="form__help">{{ $t('fields.address2_help') }}}</div>
                    </div>

                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="postcode"
                            name="postcode"
                            :placeholder="$t('fields.postcode')"
                            v-model="formData.postcode"
                        />
                        <div v-if="$v.formData.postcode.$dirty">
                            <p class="form__error" v-if="!$v.formData.postcode.required">
                                {{ $t('fields.postcode_e_required') }}
                            </p>
                        </div>
                    </div>

                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="city"
                            name="city"
                            :placeholder="$t('fields.city')"
                            v-model="formData.city"
                        />
                        <div v-if="$v.formData.city.$dirty">
                            <p class="form__error" v-if="!$v.formData.city.required">
                                {{ $t('fields.city_e_required') }}
                            </p>
                        </div>
                    </div>

                    <div class="row form__row">
                        <input
                            type="text"
                            class="form__field"
                            id="region"
                            name="region"
                            :placeholder="$t('fields.region')"
                            v-model="formData.region"
                        />

                        <div class="form__help">{{ $t('fields.region_help') }}</div>
                    </div>

                    <div class="row form__row">
                        <label class="form__label" for="country">{{ $t('fields.country') }}</label>
                        <country-select
                            class="form__field"
                            id="country"
                            name="country"
                            :preselectedValue="formData.country"
                            @input="updateCountry"
                        ></country-select>
                    </div>

                    <div class="row form__row" v-if="this.parentOrganizationId">
                        <label class="form__label" for="country">{{ $t('organizations.role_owner') }} {{ $t('fields.email') }}</label>
                        <input
                            type="text"
                            class="form__field"
                            id="email"
                            name="email"
                            :placeholder="$t('fields.email')"
                            v-model.trim="$v.formData.owner_email.$model"
                        />

                        <div v-if="$v.formData.owner_email.$error">
                            <p class="form__error" v-if="!$v.formData.owner_email.required">
                                {{ $t('fields.email_e_required') }}
                            </p>

                            <p class="form__error" v-if="!$v.formData.owner_email.email">
                                {{ $t('fields.email_e_email') }}
                            </p>
                        </div>
                    </div>
                </fieldset>
            </div>

            <dialog-buttons :submit-text="$t('buttons.save')" :disabled="loading" />
        </form>
    </div>
</template>

<script>
import * as api from '@/api';
import { required, email } from 'vuelidate/lib/validators';
import CountrySelect from '@/components/CountrySelect';
import DialogHeader from '@/components/DialogHeader';
import DialogButtons from '@/components/DialogButtons';

export default {
    components: {
        CountrySelect,
        DialogHeader,
        DialogButtons,
    },
    props: {
        organization: {
            type: [Boolean, Object],
            default: false,
        },
        parentOrganizationId: {
            type: [Boolean, String],
            default: false,
        },
    },
    data() {
        return {
            email: '',
            loading: false,
            foundUser: false,
            formData: {
                name: '',
                email: '',
                address1: '',
                address2: '',
                postcode: '',
                city: '',
                region: '',
                country: '',
                parent_id: null,
                owner_email: '',
            },
        };
    },
    validations() {
        const validations = {
            formData: {
                name: {
                    required,
                },
                email: {
                    required,
                    email,
                },
                address1: {
                    required,
                },
                postcode: {
                    required,
                },
                city: {
                    required,
                },
            },
        };

        if (this.parentOrganizationId) {
            validations.formData.owner_email = {
                required,
                email,
            };
        }

        return validations;
    },
    created() {
        this.prepareFormdata();
    },
    methods: {
        prepareFormdata() {
            if (this.organization && this.organization.id) {
                this.formData.name = this.organization.name;
                this.formData.email = this.organization.email;
                this.formData.address1 = this.organization.address1;
                this.formData.address2 = this.organization.address2;
                this.formData.postcode = this.organization.postcode;
                this.formData.city = this.organization.city;
                this.formData.region = this.organization.region;
                this.formData.country = this.organization.country;
                this.formData.parent_id = this.organization.parent_id;
            } else if (this.parentOrganizationId) {
                this.formData.parent_id = this.parentOrganizationId;
            }
        },
        updateCountry(v) {
            this.formData.country = v;
        },
        submitHandler() {
            if (this.$v.formData.$invalid) {
                this.$v.formData.$touch();
                return;
            }

            if (this.organization) {
                this.updateOrganization();
            } else {
                this.createOrganization();
            }
        },
        async updateOrganization() {
            this.loading = true;
            try {
                const response = await api.organizations.update(
                    this.organization.id,
                    this.formData
                );
                this.loading = false;
                this.$emit('close');
            } catch (error) {
                console.error(error);

				let text = this.$t('notifications.error_could_not_update_organization');

				if (error.response.data.hasOwnProperty('errors')) {
					for (const [key, value] of Object.entries(error.response.data.errors)) {
						text += '<br />' + key.toUpperCase() + ': ' + value;
					}
				}

                this.$notify({
                    type: 'error',
                    text: text,
                });
            }
        },
        async createOrganization() {
            this.loading = true;
            try {
                const response = await api.organizations.create(this.formData);
                this.loading = false;
                this.$emit('close');
            } catch (error) {
                console.error(error);

				let text = this.$t('notifications.error_could_not_create_organization');

				if (error.response.data.hasOwnProperty('errors')) {
					for (const [key, value] of Object.entries(error.response.data.errors)) {
						text += '<br />' + key.toUpperCase() + ': ' + value;
					}
				}

                this.$notify({
                    type: 'error',
                    text: text,
                });
            }
        },
    },
};
</script>
